import React, { useState } from 'react';
import { message } from 'antd';

interface IntelligenceSource {
    id: string;
    original_url: string;
    source_type: 'TRADE' | 'TENDER' | 'SUPPLY_DEMAND' | 'NEWS';
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    confidence_score: number;
    is_focus: boolean;
    ai_analysis_result: any;
    created_at: string;
}

interface ReviewCardProps {
    data: IntelligenceSource; // 情报数据对象
    onApprove: (id: string, data: any) => void; // 批准回调
    onReject: (id: string) => void; // 拒绝回调
}

/**
 * 情报审核卡片组件
 * 用于展示单条情报的详细信息，并提供编辑、批准和拒绝操作
 */
const ReviewCard: React.FC<ReviewCardProps> = ({ data, onApprove, onReject }) => {
    const [isExpanded, setIsExpanded] = useState(false); // 控制卡片展开/收起
    const [editedData, setEditedData] = useState(JSON.stringify(data.ai_analysis_result, null, 2)); // 编辑后的JSON数据

    // 根据可信度和关注状态决定边框样式
    const isHighConfidence = data.confidence_score >= 80;
    const borderColor = data.is_focus ? '#d69e2e' : (isHighConfidence ? '#38a169' : '#e2e8f0');
    const borderWidth = data.is_focus ? '2px' : '1px';

    // 来源类型中文映射
    const sourceMap: Record<string, string> = {
        'TRADE': '贸易数据',
        'TENDER': '招标公告',
        'SUPPLY_DEMAND': '供需信息',
        'NEWS': '新闻资讯',
        'REPORT': '储能报告'
    };

    return (
        <div style={{
            border: `${borderWidth} solid ${borderColor}`,
            borderRadius: '8px',
            marginBottom: '16px',
            backgroundColor: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <div style={{
                padding: '12px 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: data.is_focus ? '#fffaf0' : '#fff'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        backgroundColor: '#edf2f7',
                        color: '#4a5568'
                    }}>
                        {sourceMap[data.source_type] || data.source_type}
                    </span>
                    {data.is_focus && (
                        <span style={{
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            backgroundColor: '#fefcbf',
                            color: '#b7791f',
                            border: '1px solid #d69e2e'
                        }}>
                            承德专线
                        </span>
                    )}
                    <a href={data.original_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '14px', color: '#3182ce', textDecoration: 'none', maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {data.original_url}
                    </a>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: isHighConfidence ? '#38a169' : '#e53e3e'
                    }}>
                        可信度: {data.confidence_score}
                    </span>
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#718096' }}
                    >
                        {isExpanded ? '收起' : '展开'}
                    </button>
                </div>
            </div>

            {/* Body */}
            {isExpanded && (
                <div style={{ padding: '16px', borderTop: '1px solid #e2e8f0' }}>
                    <div style={{ marginBottom: '16px' }}>
                        <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#4a5568' }}>AI 提取数据 (JSON 编辑)</h4>
                        <textarea
                            value={editedData}
                            onChange={(e) => setEditedData(e.target.value)}
                            style={{
                                width: '100%',
                                height: '200px',
                                fontFamily: 'monospace',
                                fontSize: '12px',
                                padding: '8px',
                                borderColor: '#e2e8f0',
                                borderRadius: '4px'
                            }}
                        />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                        <button
                            onClick={() => onReject(data.id)}
                            style={{
                                padding: '8px 16px',
                                borderRadius: '4px',
                                border: '1px solid #e53e3e',
                                color: '#e53e3e',
                                background: 'white',
                                cursor: 'pointer'
                            }}
                        >
                            忽略 / 拒绝
                        </button>
                        <button
                            onClick={() => {
                                try {
                                    const parsed = JSON.parse(editedData);
                                    onApprove(data.id, parsed);
                                } catch (e) {
                                    message.error('JSON 格式错误');
                                }
                            }}
                            style={{
                                padding: '8px 16px',
                                borderRadius: '4px',
                                border: 'none',
                                backgroundColor: '#38a169',
                                color: 'white',
                                cursor: 'pointer'
                            }}
                        >
                            批准入库
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReviewCard;
